import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getOrganizationList(
    $filter: organizationFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getOrganizationList(
      filter: $filter
      pagination: $pagination
      sort: $sort
    ) {
      items {
        id
        name
        shortName
        usernamePrefix
        type
        email
        mobile
        logoUrl
        malePopulation
        femalePopulation
        thirdPopulation
        isSlaAccepted
        isArchived
      }
      pagination {
        pageNumber
        pageLimit
        totalCount
      }
      error {
        message
        statusCode
      }
    }
  }
`;

const initialState: TYPES.OrganizationListState = {
  result: null,
  error: null,
  loading: false
};

export const getOrganizationListRequest = createAsyncThunk<
  TYPES.OrganizationList,
  {
    filter?: TYPES.OrganizationFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'organization/get_organization_list',
  async (
  params: {
    filter?: TYPES.OrganizationFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getOrganizationList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getOrganizationListSlice = createSlice({
  name: 'get_organization_list',
  initialState: initialState,
  reducers: {
    getOrganizationListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getOrganizationListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getOrganizationListRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getOrganizationListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getOrganizationListClear } =
getOrganizationListSlice.actions;

export const getOrganizationList = createSelector(
  (state: RootState) => state.organization.getOrganizationList.result,
  (state: RootState) => state.organization.getOrganizationList.error,
  (state: RootState) => state.organization.getOrganizationList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getOrganizationListSlice.reducer;